import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { btnLinkCls, btnPrimaryCls, btnSecondaryCls } from "components/tw/classes";
import { CHIP_CLASS, daysSince, taskAge, Translate } from "features/cabinet-tasks/task-row";
import { fetchSignQueue, PaperItem, SignGroup, SignQueueItem } from "services/cabinet-queues.service";
import { companyOptions } from "./company-options";
import { FilterSelect, FilterToggle, SearchBox } from "./filter-controls";
import { LoadFailedNotice, QueueSkeleton } from "./queue-list-states";
import { QueueCell, QueueRow, QueueTable } from "./queue-table";
import QueueSheet from "./queue-sheet";
import {
  filterSignItems,
  OLD_AFTER_DAYS,
  SIGNATURE_OPTIONS,
  signFiltersFromQuery,
  TYPE_OPTIONS,
} from "./sign-queue-filters";
import { useQueryParams } from "./use-query-params";
import { useQueueData } from "./use-queue-data";
import { useQueueWalk } from "./use-queue-walk";

const SEARCH_DELAY_MS = 250;

const GROUP_LABEL_KEYS: Record<SignGroup, string> = {
  work: "queue_type_work",
  mine: "queue_type_mine",
  team: "queue_group_team",
};

/** «Pasirašyti dokumentus»: everything that waits for the person's signature in the app, signed one by one in the sheet. */
export default function SignQueuePage() {
  const { t } = useTranslation();
  const { companyId = "" } = useParams();
  const { params, change } = useQueryParams();
  const filters = signFiltersFromQuery(params);

  const queue = useQueueData(() => fetchSignQueue(companyId), { scopeKey: companyId });
  const walk = useQueueWalk(companyId, queue.reload);

  const allItems = queue.data?.items ?? [];
  const paper = queue.data?.paper ?? [];
  const shownItems = filterSignItems(allItems, filters);

  // The columns of the list, left to right; the cells of SignRow follow the same order.
  const columns = [
    { heading: t("queue_col_title"), width: "minmax(0,3fr)" },
    { heading: t("queue_col_type"), width: "5.5rem" },
    { heading: t("queue_col_company"), width: "minmax(0,2fr)" },
    { heading: t("queue_col_owner"), width: "minmax(0,2fr)" },
    { heading: t("queue_col_customer"), width: "minmax(0,2fr)" },
    { heading: t("queue_col_waiting"), width: "4.5rem" },
    { heading: t("queue_col_signature"), width: "5.5rem" },
    { heading: "", width: "7rem" },
  ];

  const openSheet = (items: SignQueueItem[]) =>
    walk.open({
      entries: items.map((item) => ({ kind: "sign", item })),
      total: items.length,
      offset: 0,
    });

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <p className="text-base font-semibold text-gray-900">{queue.data ? statusLine(t, allItems, shownItems) : ""}</p>
        <button
          type="button"
          className={`${btnPrimaryCls} w-full md:w-auto`}
          // Off while the list reloads (after a walk, say): the rows still on screen may include
          // documents just signed, and a walk over them would offer those again.
          disabled={shownItems.length === 0 || queue.isLoading}
          onClick={() => openSheet(shownItems)}
        >
          {t("queue_sign_start")}
        </button>
      </div>

      {/* On a phone the three drop-downs go two to a row, so the list starts higher on the first screen. */}
      <div className="grid grid-cols-2 items-end gap-3 md:flex md:flex-wrap">
        <FilterSelect
          label={t("queue_filter_type")}
          value={filters.type}
          options={TYPE_OPTIONS.map(({ value, labelKey }) => ({ value, label: t(labelKey) }))}
          onChange={(type) => change({ type: type === "all" ? "" : type })}
        />
        <FilterSelect
          label={t("queue_filter_company")}
          value={filters.companyId}
          options={companyOptions(allItems, filters.companyId, t("queue_filter_all_companies"))}
          onChange={(company) => change({ company })}
        />
        <FilterSelect
          label={t("queue_filter_signature")}
          value={filters.signature}
          options={SIGNATURE_OPTIONS.map(({ value, labelKey }) => ({ value, label: t(labelKey) }))}
          onChange={(sign) => change({ sign: sign === "all" ? "" : sign })}
        />
        <FilterToggle
          label={t("queue_filter_older", { days: OLD_AFTER_DAYS })}
          isOn={filters.olderOnly}
          onChange={(isOn) => change({ old: isOn ? "1" : "" })}
        />
        <div className="col-span-2 md:col-span-1">
          <SearchBox
            label={t("queue_search")}
            value={filters.search}
            delayMs={SEARCH_DELAY_MS}
            onSearch={(search) => change({ q: search })}
          />
        </div>
      </div>

      {queue.hasFailed && <LoadFailedNotice onRetry={queue.reload} />}
      {!queue.data && queue.isLoading && <QueueSkeleton />}

      {shownItems.length > 0 && (
        <div className={queue.isLoading ? "opacity-60" : ""}>
          <QueueTable label={t("tasks_queue_sign_work")} columns={columns} tableFrom="xl">
            {shownItems.map((item) => (
              <SignRow key={item.id} item={item} onOpen={() => openSheet([item])} />
            ))}
          </QueueTable>
        </div>
      )}

      {paper.length > 0 && <PaperSection paper={paper} />}

      <QueueSheet walk={walk} />
    </div>
  );
}

// «Viskas pasirašyta» only when nothing waits at all; when the filters hide everything the line says so.
function statusLine(t: Translate, allItems: SignQueueItem[], shownItems: SignQueueItem[]): string {
  if (allItems.length === 0) return t("queue_sign_all_signed");
  if (shownItems.length === 0) return t("queue_sign_nothing_matches");

  const oldestDays = Math.max(...shownItems.map((item) => daysSince(item.since)));
  const count = t("queue_sign_status", { count: shownItems.length });
  return oldestDays < 1 ? count : `${count} · ${t("queue_sign_oldest", { days: oldestDays })}`;
}

function SignRow({ item, onOpen }: { item: SignQueueItem; onOpen: () => void }) {
  const { t } = useTranslation();
  const waitingDays = daysSince(item.since);

  return (
    <QueueRow onOpen={onOpen}>
      <QueueCell isTitle>{item.title}</QueueCell>
      <QueueCell>
        <span className={CHIP_CLASS}>{t(GROUP_LABEL_KEYS[item.group])}</span>
      </QueueCell>
      <QueueCell label={t("queue_col_company")}>{item.companyName}</QueueCell>
      <QueueCell label={t("queue_col_owner")}>{item.ownerName}</QueueCell>
      <QueueCell label={t("queue_col_customer")}>{item.customerNames.join(", ")}</QueueCell>
      <QueueCell label={t("queue_col_waiting")}>
        <span className={waitingDays > OLD_AFTER_DAYS ? "font-medium text-amber-700" : ""}>
          {t("queue_waiting_days", { days: waitingDays })}
        </span>
      </QueueCell>
      <QueueCell>
        <span className={CHIP_CLASS}>{item.signType}</span>
      </QueueCell>
      <QueueCell className="ml-auto xl:ml-0 xl:text-right">
        <button type="button" className={btnSecondaryCls} onClick={onOpen}>
          {t("tasks_action_view")}
        </button>
      </QueueCell>
    </QueueRow>
  );
}

/** Paper documents are only listed: the person cannot sign them in the app. */
function PaperSection({ paper }: { paper: PaperItem[] }) {
  const { t } = useTranslation();
  return (
    <section aria-label={t("queue_paper_title", { count: paper.length })} className="rounded-lg bg-gray-50 p-4">
      <h2 className="text-sm font-semibold text-gray-700">{t("queue_paper_title", { count: paper.length })}</h2>
      <p className="mt-1 text-sm text-gray-600">{t("queue_paper_note")}</p>
      <ul className="mt-3 space-y-1.5">
        {paper.map((document) => (
          <li key={document.id} className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-gray-600">
            <Link to={document.link} className={btnLinkCls}>
              {document.title}
            </Link>
            <span className={CHIP_CLASS}>{document.companyName}</span>
            <span>{taskAge(document.since, t)}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}
